import { useState } from 'react';
import { Button, Input, Badge, Alert } from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { FilterBar } from '../../components/FilterBar';
import { DataTable } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { CopyChip } from '../../components/CopyChip';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { HasPermission } from '../../rbac/HasPermission';
import { useProducts, useDeactivateProduct } from './useProducts';
import { ProductFormModal } from './ProductFormModal';

const fmtEur = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });

const FILTERS = [
  { value: 'active', label: 'Nur aktive' },
  { value: 'inactive', label: 'Nur inaktive' },
  { value: 'all', label: 'Alle' },
];

/**
 * Products · CRUD.
 * Spec: orbit-specs/done/products-public-catalog.md
 *
 * DELETE setzt is_active=false (kein hard delete wegen FK von Orders/Subs).
 */
export function ProductsPage() {
  const [activeFilter, setActiveFilter] = useState('active');
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [pendingDeactivate, setPendingDeactivate] = useState(null);

  const query = useProducts();
  const deactivate = useDeactivateProduct();
  const all = query.data?.products ?? [];

  const rows = all.filter((p) => {
    if (activeFilter === 'active') return p.isActive;
    if (activeFilter === 'inactive') return !p.isActive;
    return true;
  });

  return (
    <div>
      <PageHeader
        title="Produkte"
        subtitle={`${all.filter((p) => p.isActive).length} aktiv von ${all.length} · GET /v1/admin/products`}
        actions={
          <HasPermission perms={['products.write']}>
            <Button color="primary" onClick={() => setCreating(true)}>
              + Neues Produkt
            </Button>
          </HasPermission>
        }
      />

      <Alert color="info" className="small">
        Inaktive Produkte sind in neuen Orders nicht wählbar — bestehende Subscriptions laufen
        weiter mit Snapshot-Preis. Preis-Änderungen wirken erst zur nächsten Renewal.
      </Alert>

      <FilterBar
        extras={
          <Input
            type="select"
            bsSize="sm"
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value)}
            style={{ maxWidth: 200 }}
          >
            {FILTERS.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </Input>
        }
      />

      <DataTable
        loading={query.isLoading}
        error={query.error}
        empty={!rows.length}
        emptyMessage="Keine Produkte für aktuellen Filter."
        onRetry={() => query.refetch()}
      >
        <thead>
          <tr>
            <th style={{ width: 160 }}>SKU</th>
            <th>Name</th>
            <th style={{ width: 140 }} className="text-end">Preis</th>
            <th style={{ width: 110 }}>Recurring</th>
            <th style={{ width: 90 }}>Trial</th>
            <th style={{ width: 110 }}>Mindestlfz.</th>
            <th style={{ width: 110 }}>Driver</th>
            <th style={{ width: 90 }}>Status</th>
            <th style={{ width: 180 }} />
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.id} className={!p.isActive ? 'opacity-50' : undefined}>
              <td>
                <CopyChip value={p.sku} label="SKU" />
              </td>
              <td>
                <div className="fw-semibold">{p.name}</div>
                {p.description && (
                  <div className="small text-muted" style={{ maxWidth: 320 }}>
                    {p.description}
                  </div>
                )}
              </td>
              <td className="text-end">
                <div className="fw-semibold">{fmtEur.format(p.unitAmount / 100)}</div>
                <div className="small text-muted">
                  {p.vatRate}% · {p.taxCategory}
                </div>
              </td>
              <td>
                {p.recurring ? (
                  <Badge color="info" pill>{p.billingInterval}</Badge>
                ) : (
                  <Badge color="secondary" pill>one-time</Badge>
                )}
              </td>
              <td>
                {p.trialPeriodDays > 0 ? `${p.trialPeriodDays} d` : <span className="text-muted">—</span>}
              </td>
              <td>
                {p.minimumTermMonths > 0 ? `${p.minimumTermMonths} M` : <span className="text-muted">—</span>}
              </td>
              <td>
                <code className="small">{p.driver}</code>
              </td>
              <td>
                <StatusBadge value={p.isActive ? 'active' : 'deleted'} />
              </td>
              <td className="text-end">
                <HasPermission perms={['products.write']}>
                  <Button color="primary" outline size="sm" className="me-2" onClick={() => setEditing(p)}>
                    ✏️ Bearbeiten
                  </Button>
                  {p.isActive && (
                    <Button color="warning" outline size="sm" onClick={() => setPendingDeactivate(p)}>
                      Deaktivieren
                    </Button>
                  )}
                </HasPermission>
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>

      {creating && (
        <ProductFormModal
          key="create"
          isOpen
          product={null}
          onClose={() => setCreating(false)}
        />
      )}
      {editing && (
        <ProductFormModal
          key={`edit-${editing.id}`}
          isOpen
          product={editing}
          onClose={() => setEditing(null)}
        />
      )}

      <ConfirmDialog
        isOpen={!!pendingDeactivate}
        title="Produkt deaktivieren?"
        body={
          pendingDeactivate ? (
            <>
              <p className="mb-2">
                <strong>{pendingDeactivate.name}</strong> (<code>{pendingDeactivate.sku}</code>) wird
                auf <code>is_active=false</code> gesetzt.
              </p>
              <p className="small text-muted mb-0">
                Kein Hard-Delete — bestehende Orders/Subscriptions referenzieren das Produkt weiterhin,
                neue Orders können es aber nicht mehr verwenden.
              </p>
            </>
          ) : null
        }
        confirmLabel="Deaktivieren"
        confirmColor="warning"
        busy={deactivate.isPending}
        onConfirm={async () => {
          await deactivate.mutateAsync({ id: pendingDeactivate.id });
          setPendingDeactivate(null);
        }}
        onCancel={() => setPendingDeactivate(null)}
      />
    </div>
  );
}
